/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { InstantSearch } from 'react-instantsearch-dom';

import { cssMap, jsx } from '@atlaskit/css';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { token } from '@atlaskit/tokens';

import SearchClient from './client';
import { createConstellationSearchClient, hasAlgoliaCredentials } from './create-search-client';
import { SearchModalFooter } from './footer';
import { SearchModalHeader } from './header';
import { useKeyboardNavigation } from './use-keyboard-navigation';
import { useScrollToActive } from './use-scroll-to-active';

interface SearchProps {
	isOpen: boolean;
	onClose(): void;
	testId?: string;
}

const styles = cssMap({
	modalContentsMinHeight: {
		// Keep the modal body a stable height so the header doesn't "jump" while results load.
		minHeight: '6.25rem',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	searchDisabled: {
		color: token('color.text.danger'),
		fontWeight: token('font.weight.bold'),
	},
});

/**
 * __Search__
 */
const Search = ({ isOpen, onClose, testId }: SearchProps): JSX.Element => {
	const searchClient = createConstellationSearchClient();

	const modalTestId = testId ? `${testId}-modal` : 'design-system-docs-search-modal';
	const [query, setQuery] = useState('');
	const [resultsCount, setResultsCount] = useState(0);
	const [isSearching, setIsSearching] = useState(false);
	const [shouldShowDefaultResults, setShouldShowDefaultResults] = useState(false);
	const [shouldJumpToEnd, setShouldJumpToEnd] = useState(false);
	const listboxId = testId
		? `${testId}-search-results-listbox`
		: 'design-system-docs-search-results-listbox';
	const isExpanded = (query !== '' || shouldShowDefaultResults) && !isSearching && resultsCount > 0;
	const inputRef = useRef<HTMLInputElement>(null);
	const resultRefs = useRef<(HTMLAnchorElement | null)[]>([]);
	const modalBodyRef = useRef<HTMLDivElement>(null);
	const refineRef = useRef<((query: string) => void) | null>(null);
	const prevQueryRef = useRef(query);

	const { activeIndex, handleInputKeyDown, setActiveIndex } = useKeyboardNavigation({
		query,
		resultsCount,
		resultRefs,
		onShowDefaultResults: () => {
			setShouldShowDefaultResults(true);
		},
		onShowDefaultResultsAndJumpToEnd: () => {
			setShouldShowDefaultResults(true);
			setShouldJumpToEnd(true);
		},
	});

	// Trigger empty query search when showing default results
	useEffect(() => {
		if (shouldShowDefaultResults && refineRef.current) {
			refineRef.current('');
		}
	}, [shouldShowDefaultResults]);

	// Jump to end when results load and shouldJumpToEnd is set
	useEffect(() => {
		if (shouldJumpToEnd && resultsCount > 0) {
			setActiveIndex(resultsCount - 1);
			setShouldJumpToEnd(false);
		}
	}, [shouldJumpToEnd, resultsCount, setActiveIndex]);

	const activeDescendantId = activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined;

	useScrollToActive({
		activeIndex,
		resultRefs,
		scrollViewportRef: modalBodyRef,
	});

	// Create a stable ref callback function that stores refs by index
	// Cache ref callbacks by index to ensure same index returns same function reference
	const refCallbacksRef = useRef<Map<number, (el: HTMLAnchorElement | null) => void>>(new Map());
	const getResultRef = useCallback((index: number) => {
		if (!refCallbacksRef.current.has(index)) {
			refCallbacksRef.current.set(index, (el: HTMLAnchorElement | null) => {
				resultRefs.current[index] = el;
			});
		}
		return refCallbacksRef.current.get(index)!;
	}, []);

	useEffect(() => {
		// Reset shouldShowDefaultResults when user types
		if (query) {
			setShouldShowDefaultResults(false);
			setShouldJumpToEnd(false);
		}
	}, [query]);

	useEffect(() => {
		if (!isOpen) {
			setQuery('');
			setShouldShowDefaultResults(false);
			setShouldJumpToEnd(false);
		}
	}, [isOpen]);

	useEffect(() => {
		const queryWasCleared = prevQueryRef.current && !query;
		prevQueryRef.current = query;

		if (queryWasCleared && !shouldShowDefaultResults) {
			resultRefs.current = [];
			refCallbacksRef.current.clear();
		}
	}, [query, shouldShowDefaultResults]);

	return (
		<ModalTransition>
			{isOpen && (
				<InstantSearch indexName="Components" searchClient={searchClient}>
					{/* Close button is provided by our custom header via `CloseButton` + `useModal()` */}
					{/* eslint-disable-next-line @atlaskit/design-system/use-modal-dialog-close-button */}
					<Modal
						onClose={onClose}
						autoFocus={inputRef}
						shouldScrollInViewport={false}
						testId={modalTestId}
					>
						<SearchModalHeader
							testId={testId}
							onQueryChange={setQuery}
							onKeyDown={handleInputKeyDown}
							inputRef={inputRef}
							isExpanded={isExpanded}
							listboxId={listboxId}
							activeDescendantId={activeDescendantId}
							onRefineReady={(refine) => {
								refineRef.current = refine;
							}}
						/>
						<ModalBody hasInlinePadding={false}>
							<div ref={modalBodyRef} css={styles.modalContentsMinHeight}>
								{!hasAlgoliaCredentials() && (
									<div css={styles.searchDisabled}>
										Search is not configured in this dev environment.
									</div>
								)}

								{hasAlgoliaCredentials() && (
									<SearchClient
										query={query}
										activeIndex={activeIndex}
										onResultsCountChange={setResultsCount}
										onSearchingChange={setIsSearching}
										onResultClick={onClose}
										testId={testId}
										listboxId={listboxId}
										resultsCount={resultsCount}
										shouldShowDefaultResults={shouldShowDefaultResults}
										resultRef={getResultRef}
									/>
								)}
							</div>
						</ModalBody>
						<SearchModalFooter />
					</Modal>
				</InstantSearch>
			)}
		</ModalTransition>
	);
};

export default Search;
